import { ExampleEntityIds } from '@upright/common/model/output/scores/hard-coded-entity-ids';
import { safeAssert as assert } from '@upright/common/safe-assert';
import { Dictionary, get, groupBy, mapValues, minBy, some } from 'lodash';
import { getAllParentGroupsOfUser, getUserGroupClosure } from '../../../utils';
import {
  hasLiveAgreement,
  holdsRight,
  Licence,
  matrixGroupIdsOffTheRecord,
  productKeyByFeatureId,
  productKeyByGroupId,
} from '../../entitlement/licence';
import { EntitlementKey } from '../../entitlement/registry';
import { EntitlementSource } from '../../entitlement/types';
import { SpecialUserGroupId } from '../../user-groups/types';
import { getAllAncestorsOfUserGroup } from '../../user-groups/utils';
import { UserWithIdAndPlainGroups } from '../../users/types';
import { AccessSubjectType } from '../types';
import { allFeatureAccesses } from './feature-accesses';
import { FeatureAccessMessage, FeatureAccessMessageType, FeatureId, getFeatureAccessLevel } from './types';

export const paiDemoEntityIds: Dictionary<true | undefined> = {
  [ExampleEntityIds.SIEMENS]: true,

  [ExampleEntityIds.BLACKSTONE_GROWTH_I]: true,
  [ExampleEntityIds.ATOMICO_V]: true,

  [ExampleEntityIds.ACCU_RX]: true,
  [ExampleEntityIds.AIVEN]: true,
  [ExampleEntityIds.APLO]: true,
  [ExampleEntityIds.ATTENTIVE]: true,
  [ExampleEntityIds.BIRD]: true,
  [ExampleEntityIds.BITMOVIN]: true,
  [ExampleEntityIds.JOB_AND_TALENT]: true,
  [ExampleEntityIds.KATANA]: true,
  [ExampleEntityIds.LAB_GENIUS]: true,
  [ExampleEntityIds.MASTERCLASS]: true,
  [ExampleEntityIds.METTA_SPACE]: true,
  [ExampleEntityIds.MONDOO]: true,
  [ExampleEntityIds.ONNA_TECHNOLOGIES]: true,
  [ExampleEntityIds.PACTUM]: true,
  [ExampleEntityIds.PEAKON_AP_S]: true,
  [ExampleEntityIds.PSI_QUANTUM]: true,
  [ExampleEntityIds.SCANDIT]: true,
  [ExampleEntityIds.SORARE]: true,
  [ExampleEntityIds.AUTODESK]: true,
  [ExampleEntityIds.WOLT_ENTERPRISES]: true,
  [ExampleEntityIds.Y42]: true,
  [ExampleEntityIds.ZAPP]: true,
  [ExampleEntityIds.TEMP_ENERGY]: true,
  [ExampleEntityIds._7_BREW_COFFEE]: true,
  [ExampleEntityIds.DYNAMO_SOFTWARE]: true,
  [ExampleEntityIds.EPIDEMIC_SOUND]: true,
  [ExampleEntityIds.GTCR]: true,
  [ExampleEntityIds.HYDROGEN_HEALTH]: true,
  [ExampleEntityIds.ISN_SOFTWARE]: true,
  [ExampleEntityIds.MOLLIE]: true,
  [ExampleEntityIds.OATLY]: true,
  [ExampleEntityIds.SPANX]: true,
  [ExampleEntityIds.SUPERGOOP]: true,
  [ExampleEntityIds.XPRESSBEES]: true,
};

function getDemoAccess(featureId: FeatureId, entityId: string | undefined): FeatureAccessMessageType | undefined {
  if (!entityId) {
    return undefined;
  }
  return featureId === FeatureId.EU_PAI && paiDemoEntityIds[entityId] ? FeatureAccessMessageType.AVAILABLE : undefined;
}

export function getFeatureAccessMessageType(
  featureId: FeatureId,
  user: UserWithIdAndPlainGroups | undefined,
  entityId?: string,
): FeatureAccessMessageType | undefined {
  const accessesOfUser = getAccessesOfUserByFeature(user);
  return getDemoAccess(featureId, entityId) || accessesOfUser[featureId];
}

// CLIENTS' AVAILABLE features: the base features a live contract gives an organization on the record.
export const baseFeatureIds: readonly FeatureId[] = allFeatureAccesses
  .filter(
    access =>
      (access.subjectId as SpecialUserGroupId) === SpecialUserGroupId.CLIENTS &&
      access.type === FeatureAccessMessageType.AVAILABLE,
  )
  .map(({ featureId }) => featureId);

// Features sold under a key that has no group of its own: on the record the key itself grants them.
const groupedKeys = new Set(productKeyByGroupId.values());
const featuresOfUngroupedKeys = (Object.entries(productKeyByFeatureId) as Array<[FeatureId, EntitlementKey]>).filter(
  ([, key]) => !groupedKeys.has(key),
);

/** What the licences of the user's organizations on the record grant beyond their groups' rows. */
function findLicensedFeatureAccesses(licences: readonly Licence[]): FeatureAccessMessage[] {
  const now = Date.now();
  return licences
    .flatMap(licence => [
      ...(hasLiveAgreement(licence, EntitlementSource.CONTRACT, now) ? baseFeatureIds : []),
      ...featuresOfUngroupedKeys.filter(([, key]) => holdsRight(licence, key, undefined, now)).map(([id]) => id),
    ])
    .map(featureId => ({
      featureId,
      type: FeatureAccessMessageType.AVAILABLE,
      subjectType: AccessSubjectType.USER_GROUP,
      subjectId: SpecialUserGroupId.EVERYONE,
    }));
}

function getAccessesOfUserByFeature(user: UserWithIdAndPlainGroups | undefined): Dictionary<FeatureAccessMessageType> {
  const closure = user ? getUserGroupClosure(user) : undefined;
  // Groups reached from own groups of organizations not on the record, as before.
  const legacyGroupIds = new Set<number>(
    closure
      ? [...closure.legacyGroupIds, SpecialUserGroupId.EVERYONE, SpecialUserGroupId.SIGNED_IN_USERS]
      : getAllParentGroupsOfUser(user),
  );
  const licensedGroupIds = new Set(closure?.licensedGroupIds ?? []);
  const filtered = allFeatureAccesses.filter(access => {
    if (access.subjectType === AccessSubjectType.USER_GROUP) {
      const groupId = access.subjectId;
      if (legacyGroupIds.has(groupId)) {
        return true;
      }
      // Reached only from an organization on the record: what it bought comes from its licence, so
      // a segment or its own root grants nothing AVAILABLE; their other rows apply as before.
      return (
        licensedGroupIds.has(groupId) &&
        !(access.type === FeatureAccessMessageType.AVAILABLE && matrixGroupIdsOffTheRecord.has(groupId))
      );
    }
    if (!!user && access.subjectType === AccessSubjectType.USER && access.subjectId === user.id) {
      return true;
    }
    return false;
  });
  filtered.push(...findLicensedFeatureAccesses(closure?.licences ?? []));

  const accessByFeature: Dictionary<FeatureAccessMessageType> = {};
  filtered.forEach(access => {
    const foundAccessType = get(accessByFeature, access.featureId, undefined);
    if (!foundAccessType) {
      accessByFeature[access.featureId] = access.type;
      return;
    }
    if (firstAccessIsHigher(access.type, foundAccessType)) {
      accessByFeature[access.featureId] = access.type;
      return;
    }
  });

  return accessByFeature;
}

function firstAccessIsHigher(first: FeatureAccessMessageType, second: FeatureAccessMessageType) {
  return getFeatureAccessLevel(first) < getFeatureAccessLevel(second);
}

type UserGroup = {
  id: number;
  parents?: UserGroup[];
};

export function getFeatureAccessesOfUserGroup(group: UserGroup) {
  const allAncestorGroups = getAllAncestorsOfUserGroup(group);
  // Filter accesses with subject being one of the user groups
  const featureAccesses = allFeatureAccesses.filter(access => {
    if (
      access.subjectType === AccessSubjectType.USER_GROUP &&
      ((access.subjectId as SpecialUserGroupId) === SpecialUserGroupId.EVERYONE ||
        some(allAncestorGroups, ancestorGroup => ancestorGroup.id === access.subjectId))
    ) {
      return true;
    } else {
      return false;
    }
  });

  const accessesByFeatureId = groupBy(featureAccesses, access => access.featureId);
  const strongestAccessesByFeatureId = mapValues(accessesByFeatureId, access => {
    const strongestAccess = minBy(access, a => getFeatureAccessLevel(a.type));
    assert(!!strongestAccess, 'Unexpected error.');
    return strongestAccess;
  });
  return strongestAccessesByFeatureId;
}
